import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ChevronDown, ChevronRight, Loader2, Play, Trash2 } from 'lucide-react';
import PageTitle from '@/components/PageTitle';
import { useIsInDraftMode } from '@/hooks/useDraftMode';
import {
  useAcceptSupersetRecommendation,
  useActiveSupersets,
  useDeleteSupersetRecommendation,
  useDismissSupersetRecommendation,
  useStartSupersetRecommendationRun,
  useSupersetRecommendationRun,
  useSupersetRecommendationRuns,
} from '@/hooks/useSupersetRecommendations';
import {
  ActiveSuperset,
  SupersetRecommendation,
  SupersetRecommendationRun,
} from '@/services/supersetRecommendation.service';

const formatRelative = (iso?: string | null): string => {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'just now';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

const statusBadgeVariant = (
  status: SupersetRecommendation['status'],
): 'default' | 'secondary' | 'outline' => {
  if (status === 'ACCEPTED') return 'default';
  if (status === 'DISMISSED') return 'outline';
  return 'secondary';
};

function RunSummary({
  run,
  recommendations,
}: {
  run: SupersetRecommendationRun;
  recommendations: SupersetRecommendation[];
}) {
  // "Covered by proposals" is computed live from the proposals still present
  // (dismissed ones excluded, deleted ones already gone) rather than the run's
  // frozen snapshot — so the cards never contradict the list below after an
  // accept/dismiss/delete. Per-proposal coverage is disjoint, so summing is exact.
  const coveredByProposals = recommendations
    .filter((r) => r.status !== 'DISMISSED')
    .reduce((sum, r) => sum + r.covered_analysis_count, 0);
  const coveragePct =
    run.eligible_analyses > 0
      ? Math.round((100 * coveredByProposals) / run.eligible_analyses)
      : 0;
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
      <div className="border rounded-md p-4">
        <div className="text-2xl font-semibold">{run.total_analyses}</div>
        <div className="text-xs text-muted-foreground">Analyses scanned</div>
      </div>
      <div className="border rounded-md p-4">
        <div className="text-2xl font-semibold">{run.eligible_analyses}</div>
        <div className="text-xs text-muted-foreground">Superset-eligible</div>
      </div>
      <div className="border rounded-md p-4">
        <div className="text-2xl font-semibold">{run.already_covered_analyses}</div>
        <div className="text-xs text-muted-foreground">
          Already covered today
        </div>
      </div>
      <div className="border rounded-md p-4">
        <div className="text-2xl font-semibold">{coveredByProposals}</div>
        <div className="text-xs text-muted-foreground">
          Covered by proposals
        </div>
      </div>
      <div className="border rounded-md p-4">
        <div className="text-2xl font-semibold">{coveragePct}%</div>
        <div className="text-xs text-muted-foreground">
          Of eligible workload
        </div>
      </div>
    </div>
  );
}

function IneligibleReasons({ run }: { run: SupersetRecommendationRun }) {
  const reasons = Object.entries(run.ineligible_reasons || {}).sort(
    (a, b) => b[1] - a[1],
  );
  if (reasons.length === 0) return null;
  return (
    <div className="border rounded-md p-4 mb-6">
      <h4 className="text-sm font-medium mb-2">
        Why analyses were excluded ({run.ineligible_analyses})
      </h4>
      <div className="space-y-1">
        {reasons.map(([reason, count]) => (
          <div key={reason} className="flex justify-between text-sm">
            <span className="text-muted-foreground">{reason}</span>
            <span className="font-medium ml-4 whitespace-nowrap">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function NameChips({ names, max = 12 }: { names: string[]; max?: number }) {
  const shown = names.slice(0, max);
  const extra = names.length - shown.length;
  return (
    <div className="flex flex-wrap gap-1">
      {shown.map((n, i) => (
        <Badge key={i} variant="outline" className="font-normal">
          {n}
        </Badge>
      ))}
      {extra > 0 && (
        <span className="text-xs text-muted-foreground self-center">
          +{extra} more
        </span>
      )}
    </div>
  );
}

const FRESHNESS_LABEL: Record<ActiveSuperset['freshness'], string> = {
  FRESH: 'Fresh',
  STALE: 'Stale',
  NEVER_BUILT: 'Never built',
};

function FreshnessBadge({ superset }: { superset: ActiveSuperset }) {
  const variant =
    superset.freshness === 'FRESH'
      ? 'default'
      : superset.freshness === 'STALE'
        ? 'destructive'
        : 'secondary';
  return (
    <div className="flex flex-col items-center gap-0.5">
      <Badge variant={variant}>{FRESHNESS_LABEL[superset.freshness]}</Badge>
      {superset.last_built_at && (
        <span className="text-xs text-muted-foreground">
          {formatRelative(superset.last_built_at)}
        </span>
      )}
    </div>
  );
}

function ActiveSupersetsTab() {
  const { data: supersets, isLoading } = useActiveSupersets();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ActiveSuperset | null>(null);
  const deleteRec = useDeleteSupersetRecommendation();

  const handleDelete = async () => {
    if (!deleteTarget?.recommendation_id) return;
    try {
      await deleteRec.mutateAsync(deleteTarget.recommendation_id);
      toast.success('Superset deleted', {
        description:
          'Removed from usage and its backing dataset was dropped. Covered analyses will build on their own again.',
      });
    } catch {
      toast.error('Failed to delete superset');
    } finally {
      setDeleteTarget(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading supersets…
      </div>
    );
  }

  if (!supersets || supersets.length === 0) {
    return (
      <div className="py-6 text-center text-sm text-muted-foreground">
        No active supersets yet. Accept a recommendation, or enable dashboard
        supersets, and they'll show up here.
      </div>
    );
  }

  return (
    <div className="border rounded-md">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8"></TableHead>
            <TableHead>Superset</TableHead>
            <TableHead>Source</TableHead>
            <TableHead className="text-center">Metrics</TableHead>
            <TableHead className="text-center">Dimensions</TableHead>
            <TableHead className="text-center">Covers</TableHead>
            <TableHead className="text-center">Freshness</TableHead>
            <TableHead className="w-10"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {supersets.map((s, idx) => {
            const isExpanded = expandedId === s.id;
            return (
              <>
                <TableRow
                  key={s.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setExpandedId(isExpanded ? null : s.id)}
                >
                  <TableCell className="w-8 px-2">
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </TableCell>
                  <TableCell className="font-medium">
                    {s.served_label || `Superset ${idx + 1}`}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        s.source === 'RECOMMENDATION' ? 'default' : 'secondary'
                      }
                    >
                      {s.source === 'RECOMMENDATION'
                        ? 'Recommendation'
                        : 'Dashboard'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center text-sm">
                    {s.metric_ids.length}
                  </TableCell>
                  <TableCell className="text-center text-sm">
                    {s.dimension_ids.length}
                  </TableCell>
                  <TableCell className="text-center text-sm">
                    {s.covered_analysis_count != null
                      ? `${s.covered_analysis_count} analyses`
                      : '—'}
                  </TableCell>
                  <TableCell className="text-center">
                    <FreshnessBadge superset={s} />
                  </TableCell>
                  <TableCell
                    className="w-10 px-2 text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {s.source === 'RECOMMENDATION' && s.recommendation_id && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => setDeleteTarget(s)}
                        aria-label="Delete superset"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
                {isExpanded && (
                  <TableRow key={`${s.id}-detail`}>
                    <TableCell colSpan={8} className="bg-muted/30 p-0">
                      <div className="px-6 py-4 space-y-4">
                        {s.node_missing && (
                          <div className="text-sm text-destructive">
                            Backing dataset is missing — it will be rebuilt on
                            the next build.
                          </div>
                        )}
                        <div>
                          <h4 className="text-sm font-medium mb-2">
                            Metrics ({s.metric_names.length})
                          </h4>
                          <NameChips names={s.metric_names} />
                        </div>
                        <div>
                          <h4 className="text-sm font-medium mb-2">
                            Dimensions ({s.dimension_names.length})
                          </h4>
                          <NameChips names={s.dimension_names} />
                        </div>
                        {s.hll_metric_names.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium mb-2">
                              Approximate (HyperLogLog) metrics
                            </h4>
                            <NameChips names={s.hll_metric_names} />
                            <p className="text-xs text-muted-foreground mt-1">
                              These distinct-count metrics are served
                              approximately (~2–3%) so the superset can
                              re-aggregate them.
                            </p>
                          </div>
                        )}
                        {s.node_name && (
                          <div className="text-xs text-muted-foreground">
                            Backing dataset:{' '}
                            <code className="bg-muted px-1 py-0.5 rounded">
                              {s.node_name}
                            </code>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </>
            );
          })}
        </TableBody>
      </Table>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this superset?</AlertDialogTitle>
            <AlertDialogDescription>
              “{deleteTarget?.served_label || 'This superset'}” will be removed
              from usage — the{' '}
              {deleteTarget?.covered_analysis_count ?? 0} covered analyses will
              go back to building on their own — and its backing dataset will be
              dropped. This can’t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteRec.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteRec.isPending}
            >
              {deleteRec.isPending ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function Supersets() {
  const isInDraftMode = useIsInDraftMode();
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [lookbackDays, setLookbackDays] = useState<string>('90');
  const [maxSupersets, setMaxSupersets] = useState<string>('10');
  const [maxDimensions, setMaxDimensions] = useState<string>('25');
  const [minAnalyses, setMinAnalyses] = useState<string>('2');
  const [includeProdWorkload, setIncludeProdWorkload] = useState(true);
  const [includeDraftWorkload, setIncludeDraftWorkload] = useState(false);

  const { data: runs, isLoading: runsLoading } = useSupersetRecommendationRuns();

  // Default to the most recent run once the list loads.
  useEffect(() => {
    if (!selectedRunId && runs && runs.length > 0) {
      setSelectedRunId(runs[0].id);
    }
  }, [runs, selectedRunId]);

  const selectedRunFromList = runs?.find((r) => r.id === selectedRunId) ?? null;
  const { data: runDetail, isLoading: runLoading } = useSupersetRecommendationRun(
    selectedRunId,
    { isRunning: selectedRunFromList?.status === 'RUNNING' },
  );

  const startRun = useStartSupersetRecommendationRun();
  const accept = useAcceptSupersetRecommendation();
  const dismiss = useDismissSupersetRecommendation();

  const run = runDetail?.record ?? selectedRunFromList;
  const recommendations = runDetail?.recommendations ?? [];

  const workloadBranches = [
    ...(includeProdWorkload ? ['PROD'] : []),
    ...(includeDraftWorkload ? ['DRAFT'] : []),
  ];

  const handleStartRun = async () => {
    try {
      const newRun = await startRun.mutateAsync({
        lookback_days: lookbackDays ? parseInt(lookbackDays, 10) : null,
        max_supersets: parseInt(maxSupersets, 10) || 10,
        max_dimensions_per_superset: parseInt(maxDimensions, 10) || 25,
        min_analyses_per_superset: parseInt(minAnalyses, 10) || 2,
        workload_branches: workloadBranches,
      });
      setRunDialogOpen(false);
      setSelectedRunId(newRun.id);
      toast.success('Recommendation run started');
    } catch (e) {
      toast.error('Failed to start recommendation run');
    }
  };

  const handleAccept = async (rec: SupersetRecommendation) => {
    try {
      await accept.mutateAsync(rec.id);
      toast.success('Superset added to scheduled builds', {
        description:
          'The backing dataset will be built on the next scheduled run; covered analyses will read from it automatically.',
      });
    } catch (e) {
      toast.error('Failed to accept recommendation');
    }
  };

  const handleDismiss = async (rec: SupersetRecommendation) => {
    try {
      await dismiss.mutateAsync(rec.id);
      toast.success(
        rec.status === 'ACCEPTED'
          ? 'Superset removed from scheduled builds'
          : 'Recommendation dismissed',
      );
    } catch (e) {
      toast.error('Failed to dismiss recommendation');
    }
  };

  return (
    <div className="p-8 h-full overflow-auto">
      <PageTitle title="Supersets" />

      <Tabs defaultValue="active" className="mt-4">
        <TabsList>
          <TabsTrigger value="active">Active Supersets</TabsTrigger>
          <TabsTrigger value="recommendations">Recommendations</TabsTrigger>
        </TabsList>

        <TabsContent value="recommendations" className="mt-4">
      {!isInDraftMode && (
        <div className="border rounded-md p-3 mb-4 text-sm text-muted-foreground bg-muted/30">
          You're in production mode. Supersets are edited in develop mode and
          take effect when you publish — switch to Develop Mode to analyze
          workload, accept, or dismiss recommendations.
        </div>
      )}
      <div className="flex items-center justify-end mb-4">
        <div className="flex items-center gap-2">
          {runs && runs.length > 0 && (
            <Select
              value={selectedRunId ?? undefined}
              onValueChange={(v) => {
                setSelectedRunId(v);
                setExpandedId(null);
              }}
            >
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="Select run" />
              </SelectTrigger>
              <SelectContent>
                {runs.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {formatRelative(r.started_at)} — {r.status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            onClick={() => setRunDialogOpen(true)}
            disabled={!isInDraftMode}
          >
            <Play className="h-4 w-4 mr-2" />
            Analyze Workload
          </Button>
        </div>
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        Scans your analyses (including chat-generated ones) for overlapping
        query shapes and proposes the smallest set of pre-built datasets that
        would satisfy the most queries. Accepting a proposal adds it to
        scheduled builds — covered analyses then read from the pre-built
        dataset instead of building their own.
      </p>

      {runsLoading ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading runs…
        </div>
      ) : !run ? (
        <div className="py-6 text-center text-sm text-muted-foreground">
          No recommendation runs yet. Click “Analyze Workload” to scan your
          analyses for superset opportunities.
        </div>
      ) : (
        <>
          {run.status === 'RUNNING' && (
            <div className="flex items-center py-4 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Analyzing workload… started {formatRelative(run.started_at)}
            </div>
          )}
          {run.status === 'FAILED' && (
            <div className="py-4 text-sm text-destructive">
              Run failed: {run.error || 'unknown error'}
            </div>
          )}
          {run.status === 'COMPLETE' && (
            <>
              <RunSummary run={run} recommendations={recommendations} />
              <IneligibleReasons run={run} />
            </>
          )}

          {runLoading && !runDetail ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading recommendations…
            </div>
          ) : recommendations.length === 0 && run.status === 'COMPLETE' ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No supersets to recommend — the eligible workload is either
              already covered or too heterogeneous to group under the run's
              limits.
            </div>
          ) : recommendations.length > 0 ? (
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Proposed Superset</TableHead>
                    <TableHead className="text-center">Metrics</TableHead>
                    <TableHead className="text-center">Dimensions</TableHead>
                    <TableHead className="text-center">Covers</TableHead>
                    <TableHead className="text-center">Coverage</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recommendations.map((rec, idx) => {
                    const isExpanded = expandedId === rec.id;
                    return (
                      <>
                        <TableRow
                          key={rec.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() =>
                            setExpandedId(isExpanded ? null : rec.id)
                          }
                        >
                          <TableCell className="w-8 px-2">
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                          </TableCell>
                          <TableCell className="font-medium">
                            Superset {idx + 1}
                          </TableCell>
                          <TableCell className="text-center text-sm">
                            {rec.metric_ids.length}
                          </TableCell>
                          <TableCell className="text-center text-sm">
                            {rec.dimension_ids.length}
                          </TableCell>
                          <TableCell className="text-center text-sm">
                            {rec.covered_analysis_count} analyses
                          </TableCell>
                          <TableCell className="text-center text-sm font-medium">
                            {rec.coverage_pct_of_eligible}%
                          </TableCell>
                          <TableCell>
                            <Badge variant={statusBadgeVariant(rec.status)}>
                              {rec.status}
                            </Badge>
                          </TableCell>
                          <TableCell
                            className="text-right"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {rec.status === 'PROPOSED' && (
                              <div className="flex justify-end gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleAccept(rec)}
                                  disabled={accept.isPending || !isInDraftMode}
                                >
                                  Accept
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDismiss(rec)}
                                  disabled={dismiss.isPending || !isInDraftMode}
                                >
                                  Dismiss
                                </Button>
                              </div>
                            )}
                            {rec.status === 'ACCEPTED' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDismiss(rec)}
                                disabled={dismiss.isPending || !isInDraftMode}
                              >
                                Remove from builds
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow key={`${rec.id}-detail`}>
                            <TableCell colSpan={8} className="bg-muted/30 p-0">
                              <div className="px-6 py-4 space-y-4">
                                <div>
                                  <h4 className="text-sm font-medium mb-2">
                                    Metrics ({rec.metric_names.length})
                                  </h4>
                                  <NameChips names={rec.metric_names} />
                                </div>
                                <div>
                                  <h4 className="text-sm font-medium mb-2">
                                    Dimensions ({rec.dimension_names.length})
                                  </h4>
                                  <NameChips names={rec.dimension_names} />
                                </div>
                                <div>
                                  <h4 className="text-sm font-medium mb-2">
                                    Covered analyses (
                                    {rec.covered_analysis_count})
                                  </h4>
                                  <NameChips
                                    names={rec.covered_analysis_names}
                                    max={20}
                                  />
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : null}
        </>
      )}
        </TabsContent>

        <TabsContent value="active" className="mt-4">
          <p className="text-sm text-muted-foreground mb-4">
            Datasets currently registered as supersets — from accepted
            recommendations and from dashboards. Covered analyses read from
            these instead of building their own.
          </p>
          <ActiveSupersetsTab />
        </TabsContent>
      </Tabs>

      <Dialog open={runDialogOpen} onOpenChange={setRunDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Analyze Workload</DialogTitle>
            <DialogDescription>
              Scan analyses for overlapping query shapes and propose supersets
              that cover the most queries.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-4">
            <div className="grid gap-2">
              <Label>Analyze queries from</Label>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm font-normal">
                  <Checkbox
                    checked={includeProdWorkload}
                    onCheckedChange={(v) => setIncludeProdWorkload(v === true)}
                  />
                  Production
                </label>
                <label className="flex items-center gap-2 text-sm font-normal">
                  <Checkbox
                    checked={includeDraftWorkload}
                    onCheckedChange={(v) => setIncludeDraftWorkload(v === true)}
                  />
                  Draft
                </label>
              </div>
              <p className="text-xs text-muted-foreground">
                Which branch's query workload to analyze. Recommendations are
                created in your current branch either way — accept in develop
                mode, then publish to take effect in production.
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="lookback-days">Lookback (days)</Label>
              <Input
                id="lookback-days"
                type="number"
                value={lookbackDays}
                onChange={(e) => setLookbackDays(e.target.value)}
                placeholder="Empty = all time"
              />
              <p className="text-xs text-muted-foreground">
                Only consider analyses created (or last run) in this window.
                Leave empty for all time.
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="max-supersets">Max supersets</Label>
              <Input
                id="max-supersets"
                type="number"
                value={maxSupersets}
                onChange={(e) => setMaxSupersets(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="max-dimensions">
                Max dimensions per superset
              </Label>
              <Input
                id="max-dimensions"
                type="number"
                value={maxDimensions}
                onChange={(e) => setMaxDimensions(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Wider grains cover more queries but pre-aggregate less. Counts
                only the dimensions your queries use — required calendar and
                security dimensions are always included on top.
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="min-analyses">Min analyses per superset</Label>
              <Input
                id="min-analyses"
                type="number"
                value={minAnalyses}
                onChange={(e) => setMinAnalyses(e.target.value)}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRunDialogOpen(false)}
              disabled={startRun.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleStartRun}
              disabled={startRun.isPending || workloadBranches.length === 0}
            >
              {startRun.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Start Analysis
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
